import { useSubAppNavigation } from "@/hooks/use-sub-app-nav";
import { getToken } from "@/lib/utils/auth-storage";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import "./AgentOSSection.css";

export interface AgentOSSectionProps {
  t: (en: string, zh: string) => string;
  onRequestLogin?: (redirect?: "toolbox") => void;
  onExploreProducts?: () => void;
  isNavigating?: boolean;
}

type ProductId = "hub" | "sim" | "gen" | "med";

interface ProductConfig {
  id: ProductId;
  index: number;
  name: string;
  badge?: { en: string; zh: string };
  tagline: { en: string; zh: string };
  metrics: Array<{
    value: { en: string; zh: string };
    label: { en: string; zh: string };
  }>;
  btnText: { en: string; zh: string };
}

const PLANET_IMAGE =
  "https://photog-prod.oss-accelerate.aliyuncs.com/uploads/public/report/20260904/781c160a0c15497d9b0eb633c7cb4e7c.png";

export const AgentOSSection: React.FC<AgentOSSectionProps> = ({
  t,
  onRequestLogin,
  onExploreProducts,
  isNavigating: propsIsNavigating,
}) => {
  const [activeId, setActiveId] = useState<ProductId>("sim");
  const stageRef = useRef<HTMLDivElement>(null);
  const { navigating: localNavigating, navigate } = useSubAppNavigation();
  const isNavigating = propsIsNavigating ?? localNavigating;

  const handleEnter = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (onExploreProducts) {
      onExploreProducts();
      return;
    }

    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "cta_click", {
        event_category: "agent_os",
        event_label: `enter_${currentProduct.name.toLowerCase()}`,
        button_text: `${currentProduct.btnText.en} / ${currentProduct.btnText.zh}`,
      });
    }

    const token = getToken();
    if (!token) {
      onRequestLogin?.("toolbox");
      return;
    }
    await navigate("toolbox");
  };

  const products: Record<ProductId, ProductConfig> = {
    hub: {
      id: "hub",
      index: 0,
      name: "OranHub",
      tagline: { en: "Precision Insights", zh: "精准洞察" },
      metrics: [
        {
          value: { en: "6 Dimensions", zh: "6 维测算" },
          label: { en: "Brand Health Matrix", zh: "品牌健康度" },
        },
        {
          value: { en: "Audience · Rival · Strategy", zh: "人群 · 竞品 · 策略" },
          label: { en: "Target Profiles", zh: "洞察对象" },
        },
        {
          value: { en: "Minute-level", zh: "分钟级" },
          label: { en: "Instant Reports", zh: "报告产出" },
        },
        {
          value: { en: "Bound · Range · Risk", zh: "边界 · 区间 · 风险" },
          label: { en: "Strategic Output", zh: "策略输出" },
        },
      ],
      btnText: { en: "Enter OranHub", zh: "进入 OranHub" },
    },
    sim: {
      id: "sim",
      index: 1,
      name: "OranSim",
      badge: { en: "Flagship", zh: "主打产品" },
      tagline: { en: "Extreme Prediction", zh: "极致预测" },
      metrics: [
        {
          value: { en: "100K Level", zh: "10 万级" },
          label: { en: "Encapsulated Audience", zh: "封装人群" },
        },
        {
          value: { en: "1M Level", zh: "100 万级" },
          label: { en: "Simulated Market", zh: "模拟市场" },
        },
        {
          value: { en: "64 Nodes", zh: "64 节点" },
          label: { en: "Causal Graph", zh: "因果图" },
        },
        {
          value: { en: "R² 0.39–0.62", zh: "R² 0.39–0.62" },
          label: { en: "Prediction Accuracy", zh: "预测精度" },
        },
      ],
      btnText: { en: "Enter OranSim", zh: "进入 OranSim" },
    },
    gen: {
      id: "gen",
      index: 2,
      name: "OranGen",
      tagline: { en: "High-Efficiency Production", zh: "高效量产" },
      metrics: [
        {
          value: { en: "150 → 10 Mins", zh: "150 → 10 分钟" },
          label: { en: "Time per Video", zh: "单条耗时" },
        },
        {
          value: { en: "15×", zh: "15×" },
          label: { en: "Efficiency Boost", zh: "效率提升" },
        },
        {
          value: { en: "30+ Videos", zh: "30+ 条" },
          label: { en: "Single Output Batch", zh: "单次产出" },
        },
        {
          value: { en: "TikTok · IG · Shorts", zh: "TikTok · IG · Shorts" },
          label: { en: "Platform Adaptation", zh: "平台适配" },
        },
      ],
      btnText: { en: "Enter OranGen", zh: "进入 OranGen" },
    },
    med: {
      id: "med",
      index: 3,
      name: "OranMed",
      tagline: { en: "Extreme Distribution", zh: "极致分发" },
      metrics: [
        {
          value: { en: "4000+", zh: "4000+" },
          label: { en: "Matrix Scale", zh: "矩阵规模" },
        },
        {
          value: { en: "$0.40", zh: "$0.40" },
          label: { en: "CPM Cost", zh: "千次曝光" },
        },
        {
          value: { en: "$0.06", zh: "$0.06" },
          label: { en: "Cost per Engagement", zh: "单次互动" },
        },
        {
          value: { en: "10× Industry", zh: "行业 10 倍" },
          label: { en: "Unit Efficiency", zh: "单位效率" },
        },
      ],
      btnText: { en: "Enter OranMed", zh: "进入 OranMed" },
    },
  };

  const satelliteNodes: ProductId[] = ["hub", "gen", "med"];
  const STEP = 120; // 严格等距 120° 角分布
  const BASE_OFFSET = 210;
  const FRONT_PHASE = 80; // 正前方近景焦点相位

  // 初始将 OranHub 精准对齐到正前方焦点
  const initialAngle = FRONT_PHASE - BASE_OFFSET;
  const [orbitAngle, setOrbitAngle] = useState(initialAngle);
  const currentAngleRef = useRef(initialAngle);
  const targetAngleRef = useRef(initialAngle);
  const isHoveringRef = useRef(false);
  const wheelLockRef = useRef(0);

  const currentProduct = products[activeId];

  // 切换产品：沿 3D 椭圆轨迹平滑公转旋转至正前方焦点
  const switchProduct = (id: ProductId) => {
    setActiveId(id);
    const idx = satelliteNodes.indexOf(id);
    if (idx > -1) {
      const target = FRONT_PHASE - (idx * STEP + BASE_OFFSET);
      const diff = (((target - targetAngleRef.current) % 360) + 540) % 360 - 180;
      targetAngleRef.current += diff;
    }
  };

  // 60FPS 连续 3D 椭圆轨道公转动力学引擎（带平滑自发公转与滚轮曲线公转）
  useEffect(() => {
    let animationFrameId: number;
    let lastTime = performance.now();

    const loop = (time: number) => {
      const dt = Math.min((time - lastTime) / 1000, 0.1);
      lastTime = time;

      if (!isHoveringRef.current) {
        // 自发自然巡航公转（每秒约 5 度，持续优雅环绕）
        targetAngleRef.current += dt * 5;
      }

      // 弹簧阻尼平滑插值（Lerp: 0.085）
      const diff = targetAngleRef.current - currentAngleRef.current;
      currentAngleRef.current += diff * 0.085;
      setOrbitAngle(currentAngleRef.current);

      animationFrameId = requestAnimationFrame(loop);
    };

    animationFrameId = requestAnimationFrame(loop);

    // 滚轮驱动公转：沿 3D 椭圆弧线轨迹驱动三大卫星飞速公转，并将新选中的卫星精准滑行至正前方
    const stage = stageRef.current;
    if (!stage) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const now = performance.now();
      if (now - wheelLockRef.current < 260 || Math.abs(e.deltaY) < 6) return;
      wheelLockRef.current = now;

      const dir = e.deltaY > 0 ? 1 : -1;

      setActiveId((curr) => {
        let at = satelliteNodes.indexOf(curr);
        if (at === -1) at = 0;
        const nextIdx = (at + dir + satelliteNodes.length) % satelliteNodes.length;
        const nextId = satelliteNodes[nextIdx];

        // 沿椭圆弧线旋转轨道，使新选中的卫星精准滑行至最前方焦点
        const target = FRONT_PHASE - (nextIdx * STEP + BASE_OFFSET);
        const diff = (((target - targetAngleRef.current) % 360) + 540) % 360 - 180;
        targetAngleRef.current += diff;

        return nextId;
      });
    };

    stage.addEventListener("wheel", handleWheel, { passive: false, capture: true });

    return () => {
      cancelAnimationFrame(animationFrameId);
      stage.removeEventListener("wheel", handleWheel, { capture: true });
    };
  }, []);

  // 渲染右侧暗色控制台的可视化流程面板 (UI 模板原版 S 型波浪排版、单粒子平缓流动与全量 i18n 支持)
  const renderVisualizer = (id: ProductId) => {
    switch (id) {
      case "hub":
        return (
          <div className="product-viz hub-viz">
            {/* 动态连接虚线与沿空隙飞跃的粒子 */}
            <svg className="viz-flow" viewBox="0 0 560 250" preserveAspectRatio="none" aria-hidden="true">
              <path d="M140 126 C150 126 160 126 174 126 M370 126 C390 126 404 126 420 126" />
              <circle cx="157" cy="126" r="2.2" fill="#FFE6C8" className="animate-pulse" />
              <circle cx="395" cy="126" r="2.2" fill="#FFE6C8" className="animate-pulse" />

              {/* 仅在节点缝隙处飞跃的流动粒子：进入节点物理边界彻底归零消失(r=0, opacity=0) */}
              <circle fill="#FFE6C8" filter="url(#singleParticleGlow)">
                <animateMotion
                  dur="6.5s"
                  repeatCount="indefinite"
                  path="M 130 126 L 180 126 M 360 126 L 430 126"
                  keyPoints="0;1"
                  keyTimes="0;1"
                  calcMode="linear"
                />
                <animate
                  attributeName="r"
                  dur="6.5s"
                  repeatCount="indefinite"
                  values="0; 2.8; 2.8; 0; 0; 2.8; 2.8; 0; 0"
                  keyTimes="0; 0.05; 0.38; 0.42; 0.58; 0.62; 0.95; 1; 1"
                  calcMode="linear"
                />
                <animate
                  attributeName="opacity"
                  dur="6.5s"
                  repeatCount="indefinite"
                  values="0; 1; 1; 0; 0; 1; 1; 0; 0"
                  keyTimes="0; 0.05; 0.38; 0.42; 0.58; 0.62; 0.95; 1; 1"
                  calcMode="linear"
                />
              </circle>
            </svg>

            {/* 节点 1：品牌输入 (UI 模板原版拟物微交互输入框) */}
            <div className="hub-brand flow-node" tabIndex={0}>
              <span className="viz-name">{t("Brand Input", "品牌输入")}</span>
              <div className="hub-input">
                <i />
                <span className="brand-demo-input" aria-label="Brand demo input box">
                  <i /><i /><i /><b /><b />
                </span>
              </div>

              <div className="node-detail">
                <h4>{t("Brand Input", "品牌输入")}</h4>
                <p className="detail-row"><b>{t("Brand", "品牌")}</b><span>rhode</span></p>
                <p className="detail-row"><b>{t("Platform", "平台")}</b><span>TikTok</span></p>
                <p className="detail-row"><b>{t("Date", "报告日期")}</b><span>2026-07-10</span></p>
                <p className="detail-row"><b>{t("Type", "报告类型")}</b><span>{t("Brand Health", "品牌健康度")}</span></p>
                <p className="detail-row"><b>{t("Scale", "账号规模")}</b><span>{t("Official Followers 2.3M", "官方粉丝 2.3M")}</span></p>
              </div>
            </div>

            {/* 节点 2：健康度报告 (六维雷达图 - UI 原版几何坐标与动态生长) */}
            <div className="hub-radar flow-node" tabIndex={0}>
              <span className="viz-name">{t("Health Matrix", "健康度报告")}</span>
              <svg viewBox="0 0 240 210" aria-hidden="true">
                <g transform="translate(120 102)">
                  <polygon className="radar-grid" points="0,-75 65,-37.5 65,37.5 0,75 -65,37.5 -65,-37.5" />
                  <polygon className="radar-grid" points="0,-55 47.6,-27.5 47.6,27.5 0,55 -47.6,27.5 -47.6,-27.5" />
                  <polygon className="radar-grid" points="0,-35 30.3,-17.5 30.3,17.5 0,35 -30.3,17.5 -30.3,-17.5" />
                  <path className="radar-axis" d="M0 0V-75 M0 0L65-37.5 M0 0L65 37.5 M0 0V75 M0 0L-65 37.5 M0 0L-65-37.5" />
                  <polygon className="radar-area" points="0,-64 51,-29 44,25 0,48 -54,31 -43,-25" />
                  <circle className="radar-node" style={{ animationDelay: "220ms" }} cx="0" cy="-64" r="3" />
                  <circle className="radar-node" style={{ animationDelay: "280ms" }} cx="51" cy="-29" r="3" />
                  <circle className="radar-node" style={{ animationDelay: "340ms" }} cx="44" cy="25" r="3" />
                  <circle className="radar-node" style={{ animationDelay: "400ms" }} cx="0" cy="48" r="3" />
                  <circle className="radar-node" style={{ animationDelay: "460ms" }} cx="-54" cy="31" r="3" />
                  <circle className="radar-node" style={{ animationDelay: "520ms" }} cx="-43" cy="-25" r="3" />
                </g>
              </svg>

              <div className="node-detail">
                <h4>{t("Health Matrix", "健康度报告")}</h4>
                <p className="detail-row"><b>{t("Total", "总分")}</b><span>67.73 / 100</span></p>
                <p className="detail-row"><b>{t("Advantage", "优势")}</b><span>{t("Role Interaction Efficiency 100.0", "角色互动效率 100.0")}</span></p>
                <p className="detail-row"><b>{t("Risk", "风险")}</b><span>{t("Competitor Posture 43.33", "竞品位势 43.33")}</span></p>
              </div>
            </div>

            {/* 节点 3：策略建议 (圆形发光仪表盘 - 黄金适中比例) */}
            <div className="hub-strategy flow-node" tabIndex={0}>
              <span className="viz-name">{t("Strategy", "策略建议")}</span>
              <div className="strategy-path" aria-hidden="true">
                <svg className="strategy-gauge" viewBox="0 0 120 120">
                  <circle className="gauge-track" cx="60" cy="60" r="39" pathLength="100" transform="rotate(-90 60 60)" />
                  <circle className="gauge-progress" cx="60" cy="60" r="39" pathLength="100" transform="rotate(-90 60 60)" />
                </svg>
                <span className="strategy-score"><b>68%</b></span>
              </div>

              <div className="node-detail">
                <h4>{t("Strategy", "策略建议")}</h4>
                <p className="detail-row"><b>{t("Score", "得分")}</b><span>67.7 / 100</span></p>
                <p className="detail-row"><b>{t("Strength", "优势")}</b><span>{t("High Engagement", "互动质量高")}</span></p>
                <p className="detail-row"><b>{t("Shortboard", "短板")}</b><span>{t("Competitor & Conversion Weak", "竞品与转化偏弱")}</span></p>
                <p className="detail-row"><b>{t("Advice", "建议")}</b><span>{t("Strengthen official & KOL content to boost search conversion", "加强官方与 KOL 内容，提升搜索转化")}</span></p>
              </div>
            </div>
          </div>
        );

      case "sim":
        return (
          <div className="product-viz sim-viz">
            {/* 动态连接波浪曲线导线与沿线流动粒子 (断续桥接节点，不贯穿节点内部，1个发光粒子平缓运行) */}
            <svg className="viz-flow" viewBox="0 0 560 250" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <linearGradient id="forecastBand" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#F97316" stopOpacity="0.06" />
                  <stop offset="1" stopColor="#F97316" stopOpacity="0.30" />
                </linearGradient>
                <filter id="singleParticleGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2.2" result="glow" />
                  <feMerge>
                    <feMergeNode in="glow" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* 仅在节点间空隙进行局部桥接的虚线 (不穿透节点内部，保持各节点占位干净独立) */}
              <path
                d="M 92 136 C 112 136, 120 108, 130 108 M 268 108 C 278 108, 286 134, 296 134 M 405 134 C 422 134, 430 116, 444 116"
                fill="none"
                stroke="rgba(255,200,150,0.34)"
                strokeWidth="1.1"
                strokeDasharray="3 5"
              />

              {/* 节点过渡处的定位微光点 */}
              <circle cx="111" cy="122" r="2.2" fill="#FFE6C8" className="animate-pulse" />
              <circle cx="282" cy="121" r="2.2" fill="#FFE6C8" className="animate-pulse" />
              <circle cx="424" cy="125" r="2.2" fill="#FFE6C8" className="animate-pulse" />

              {/* 仅保留 1 个粒子：仅在节点间空隙虚线飞行时显现，进入节点物理边界后半径与透明度彻底归零(消失不见)，离开节点后再次浮现 */}
              <circle fill="#FFE6C8" filter="url(#singleParticleGlow)">
                <animateMotion
                  dur="8s"
                  repeatCount="indefinite"
                  path="M 85 136 C 115 136, 125 108, 195 108 C 265 108, 280 134, 345 134 C 405 134, 420 116, 475 116"
                  keyPoints="0;1"
                  keyTimes="0;1"
                  calcMode="linear"
                />
                {/* 半径双重归零动画：在节点内部彻底消失为 0px，在节点间缝隙正常放大至 3px */}
                <animate
                  attributeName="r"
                  dur="8s"
                  repeatCount="indefinite"
                  values="0; 0; 3; 3; 0; 0; 3; 3; 0; 0; 3; 3; 0; 0"
                  keyTimes="0; 0.02; 0.04; 0.11; 0.13; 0.46; 0.48; 0.53; 0.55; 0.81; 0.83; 0.90; 0.92; 1"
                  calcMode="linear"
                />
                {/* 透明度同步归零动画：离开节点平滑浮现，进入节点卡片彻底不可见 */}
                <animate
                  attributeName="opacity"
                  dur="8s"
                  repeatCount="indefinite"
                  values="0; 0; 1; 1; 0; 0; 1; 1; 0; 0; 1; 1; 0; 0"
                  keyTimes="0; 0.02; 0.04; 0.11; 0.13; 0.46; 0.48; 0.53; 0.55; 0.81; 0.83; 0.90; 0.92; 1"
                  calcMode="linear"
                />
              </circle>
            </svg>

            {/* 节点 1：Campaign 输入 (下沉 top: 28px) */}
            <div className="sim-brief flow-node" tabIndex={0}>
              <span className="viz-name">{t("Campaign Input", "Campaign 输入")}</span>
              <div className="sim-param"><span>{t("Growth Goal", "增长目标")}</span><b>{t("Conversion", "增长转化")}</b></div>
              <div className="sim-param"><span>{t("Target Sample", "目标样本")}</span><b>{t("100K-level", "10 万级")}</b></div>
              <div className="sim-param"><span>{t("Sim Market", "模拟市场")}</span><b>{t("1M-level", "100 万级")}</b></div>

              <div className="node-detail">
                <h4>{t("Campaign Input", "Campaign 输入")}</h4>
                <p className="detail-row"><b>{t("Content", "内容")}</b><span>{t("Copy & Product Intro", "文案与产品简介")}</span></p>
                <p className="detail-row"><b>{t("Audience", "人群")}</b><span>{t("Age, Gender, City Tier", "年龄、性别、城市层级")}</span></p>
                <p className="detail-row"><b>{t("Channel", "渠道")}</b><span>{t("Multi-platform Budget", "多平台预算配置")}</span></p>
                <p className="detail-row"><b>{t("Variable", "变量")}</b><span>{t("KOL, Creative & AI Models", "KOL、创意与 AI 模型")}</span></p>
              </div>
            </div>

            {/* 节点 2：群体智能模拟 (上浮 top: -18px) */}
            <div className="sim-swarm flow-node" tabIndex={0}>
              <span className="viz-name">{t("Swarm Simulation", "群体智能模拟")}</span>
              <div className="response-field" aria-hidden="true">
                <span className="response-cluster c1" />
                <span className="response-cluster c2" />
                <span className="response-cluster c3" />
                <span className="response-core" />
                {[
                  { x: "7%", y: "31%", delay: "-120ms" },
                  { x: "16%", y: "22%", delay: "-480ms" },
                  { x: "24%", y: "39%", delay: "-810ms" },
                  { x: "12%", y: "52%", delay: "-260ms" },
                  { x: "27%", y: "57%", delay: "-650ms" },
                  { x: "4%", y: "67%", delay: "-930ms" },
                  { x: "20%", y: "72%", delay: "-350ms" },
                  { x: "31%", y: "29%", delay: "-740ms" },
                  { x: "39%", y: "48%", delay: "-180ms" },
                  { x: "46%", y: "63%", delay: "-520ms" },
                  { x: "54%", y: "72%", delay: "-870ms" },
                  { x: "62%", y: "57%", delay: "-320ms" },
                  { x: "58%", y: "39%", delay: "-690ms" },
                  { x: "43%", y: "81%", delay: "-1020ms" },
                  { x: "68%", y: "73%", delay: "-410ms" },
                  { x: "35%", y: "66%", delay: "-780ms" },
                  { x: "73%", y: "19%", delay: "-220ms" },
                  { x: "82%", y: "11%", delay: "-560ms" },
                  { x: "91%", y: "24%", delay: "-900ms" },
                  { x: "78%", y: "35%", delay: "-370ms" },
                  { x: "94%", y: "43%", delay: "-710ms" },
                  { x: "70%", y: "48%", delay: "-1060ms" },
                  { x: "86%", y: "54%", delay: "-450ms" },
                  { x: "96%", y: "63%", delay: "-830ms" },
                ].map((agent, idx) => (
                  <i
                    key={idx}
                    className="response-agent"
                    style={{
                      left: agent.x,
                      top: agent.y,
                      animationDelay: agent.delay,
                    }}
                  />
                ))}
              </div>

              <div className="node-detail">
                <h4>{t("Swarm Simulation", "群体智能模拟")}</h4>
                <p className="detail-row"><b>{t("Scale", "规模")}</b><span>{t("100K LLM Agents", "10 万 LLM Agent")}</span></p>
                <p className="detail-row"><b>{t("Attribute", "属性")}</b><span>{t("Demographics & Persona", "年龄、城市与人设")}</span></p>
                <p className="detail-row"><b>{t("Deduction", "推演")}</b><span>{t("Counterfactual Simulations", "多场景反事实实验")}</span></p>
                <p className="detail-row"><b>{t("Output", "输出")}</b><span>{t("Agent Response & Emergence", "个体反应与群体涌现")}</span></p>
              </div>
            </div>

            {/* 节点 3：群体星图 (下沉 top: 26px，文字在正上方绝不被遮挡) */}
            <div className="sim-map flow-node" tabIndex={0}>
              <span className="viz-name">{t("Swarm Nebula", "群体星图")}</span>
              <img
                className="group-nebula"
                src="https://photog-prod.oss-accelerate.aliyuncs.com/uploads/public/report/20260903/763226b2c3f14497baad2d08d9411c0a.png"
                alt=""
                aria-hidden="true"
              />

              <div className="node-detail">
                <h4>{t("Swarm Nebula", "群体星图")}</h4>
                <p className="detail-row"><b>{t("Layer", "分层")}</b><span>{t("City Tier & Interest Clusters", "城市等级与兴趣聚类")}</span></p>
                <p className="detail-row"><b>{t("Individual", "个体")}</b><span>{t("Agent Profile & Reasoning", "Agent 档案与推理链")}</span></p>
                <p className="detail-row"><b>{t("Sample", "样本")}</b><span>{t("KOL & Key Node Samples", "KOL 与关键节点抽样")}</span></p>
                <p className="detail-row"><b>{t("View", "视图")}</b><span>{t("Dynamics & Evolution", "群体关系与演化轨迹")}</span></p>
              </div>
            </div>

            {/* 节点 4：预测报告 (上浮 top: -18px) */}
            <div className="sim-forecast flow-node" tabIndex={0}>
              <span className="viz-name">{t("Forecast Report", "预测报告")}</span>
              <svg className="forecast-chart" viewBox="0 0 160 96" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                <path className="forecast-grid" d="M3 24H157M3 50H157M3 76H157" />
                <path className="forecast-split" d="M92 14V84" />
                <path className="forecast-band" d="M92 45C111 36 132 22 157 15L157 58C132 57 111 61 92 56Z" />
                <path className="forecast-bound" d="M92 45C111 36 132 22 157 15M92 56C111 61 132 57 157 58" />
                <path className="forecast-mid" d="M3 72C18 68 30 51 43 57S64 63 76 53S87 50 92 50C113 45 134 36 157 29" />
                <circle className="forecast-node" cx="43" cy="57" r="2.4" />
                <circle className="forecast-node" cx="92" cy="50" r="2.7" />
                <circle className="forecast-node is-end" cx="157" cy="29" r="3.2" />
                <text className="forecast-delta" x="126" y="15">+18%</text>
              </svg>

              <div className="node-detail">
                <h4>{t("Forecast Report", "预测报告")}</h4>
                <p className="detail-row"><b>{t("Metrics", "指标")}</b><span>{t("CTR, CVR, ROI, Reach", "CTR、CVR、ROI、触达")}</span></p>
                <p className="detail-row"><b>{t("Contrast", "对比")}</b><span>{t("Baseline vs Optimized", "基线与调整方案")}</span></p>
                <p className="detail-row"><b>{t("Interval", "区间")}</b><span>{t("Trends, Bounds & Risks", "趋势、边界与风险")}</span></p>
                <p className="detail-row"><b>{t("Decision", "决策")}</b><span>{t("Budget & Strategic Advice", "预算与策略建议")}</span></p>
              </div>
            </div>
          </div>
        );

      case "gen":
        return (
          <div className="product-viz gen-viz gen-workflow">
            <div className="gen-workflow-track">
              {/* 阶段 1：对标视频 */}
              <div className="gen-phase gen-reference-phase flow-node" tabIndex={0}>
                <span className="viz-name">{t("Reference Video", "对标视频")}</span>
                <div className="video-card gen-reference">
                  <video
                    className="gen-media"
                    src="https://photog-prod.oss-accelerate.aliyuncs.com/uploads/public/20260903-175120.mp4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label="爆款参考视频"
                  />
                  <span className="scanlines" />
                </div>
                <div className="node-detail">
                  <h4>{t("Viral Video", "爆款视频")}</h4>
                  <p className="detail-row"><b>{t("Opening Hook", "开场钩子")}</b><span>{t("Quick Hook Attention", "快速吸引注意")}</span></p>
                  <p className="detail-row"><b>{t("Product Demo", "产品展示")}</b><span>{t("Build Product Awareness", "建立产品认知")}</span></p>
                  <p className="detail-row"><b>{t("Highlight USP", "卖点强化")}</b><span>{t("Accentuate Core Strengths", "突出核心优势")}</span></p>
                  <p className="detail-row"><b>{t("Conversion CTA", "转化引导")}</b><span>{t("Drive User Action", "推动用户行动")}</span></p>
                </div>
              </div>

              {/* 连接器 1 */}
              <div className="gen-connector" aria-hidden="true">
                <i />
              </div>

              {/* 阶段 2：产品信息 */}
              <div className="gen-phase gen-product-phase flow-node" tabIndex={0}>
                <span className="viz-name">{t("Product Spec", "产品信息")}</span>
                <div className="gen-product-compact">
                  <span className="product-thumb" aria-hidden="true" />
                  <span className="brand-demo-input" aria-label="产品信息输入状态">
                    <i /><i /><i /><b /><b />
                  </span>
                  <span className="gen-product-lines" aria-hidden="true">
                    <i /><i /><i />
                  </span>
                </div>
                <div className="node-detail">
                  <h4>{t("Product Spec", "产品信息")}</h4>
                  <p className="detail-row"><b>{t("Product", "产品")}</b><span>Sony WH-1000XM5</span></p>
                  <p className="detail-row"><b>{t("Category", "品类")}</b><span>{t("Over-ear ANC Headphones", "头戴式无线降噪耳机")}</span></p>
                  <p className="detail-row"><b>{t("Battery", "续航")}</b><span>{t("30 Hours (ANC On)", "30 小时（降噪开启）")}</span></p>
                  <p className="detail-row"><b>{t("Fast Charge", "快充")}</b><span>{t("Charge 3m · Play 3h", "充电 3 分钟 · 播放 3 小时")}</span></p>
                  <p className="detail-row"><b>{t("Audience", "人群")}</b><span>{t("Commute · Travel · Office", "通勤 · 差旅 · 办公")}</span></p>
                  <p className="detail-row"><b>{t("Key Selling Point", "卖点")}</b><span>{t("Dual Chips · 8-Mic ANC", "双处理器 · 8 麦克风降噪")}</span></p>
                </div>
              </div>

              {/* 连接器 2 (复刻分支 1:N) */}
              <div className="gen-connector gen-replicate" aria-hidden="true">
                <i />
              </div>

              {/* 阶段 3：复刻视频 (双卡片并列) */}
              <div className="gen-phase gen-output-phase flow-node" tabIndex={0}>
                <span className="viz-name">{t("Replicate Videos", "复刻视频")}</span>
                <div className="gen-output-pair">
                  <div className="video-card r1" style={{ ["--rd" as string]: "180ms" }}>
                    <video
                      className="gen-media"
                      src="https://photog-prod.oss-accelerate.aliyuncs.com/uploads/public/20260903-175149.mp4"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      aria-label="复刻视频一"
                    />
                  </div>
                  <div className="video-card r3" style={{ ["--rd" as string]: "300ms" }}>
                    <video
                      className="gen-media"
                      src="https://photog-prod.oss-accelerate.aliyuncs.com/uploads/public/20260903-175154.mp4"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="metadata"
                      aria-label="复刻视频二"
                    />
                  </div>
                </div>
                <div className="node-detail">
                  <h4>{t("Replicate Videos", "复刻视频")}</h4>
                  <p className="detail-row"><b>{t("Hook Replication", "钩子复刻")}</b><span>{t("Retain Viral Pacing", "延续原视频节奏")}</span></p>
                  <p className="detail-row"><b>{t("Product Insertion", "产品替换")}</b><span>{t("Embed Target Product", "植入当前产品")}</span></p>
                  <p className="detail-row"><b>{t("USP Recombination", "卖点重组")}</b><span>{t("Align Core Selling Points", "匹配产品核心卖点")}</span></p>
                  <p className="detail-row"><b>{t("Ending Generation", "结尾生成")}</b><span>{t("Complete Conversion Call", "完成转化表达")}</span></p>
                </div>
              </div>
            </div>
          </div>
        );

      case "med":
        return (
          <div className="product-viz med-viz">
            {/* 动态连接虚线与沿空隙飞跃的粒子 (一进二出分岔流转) */}
            <svg className="mg-wires" viewBox="0 0 520 260" preserveAspectRatio="none" aria-hidden="true">
              <path className="w" d="M117 126 C 133 126, 140 122, 152 122" />
              <path className="w" d="M331 104 C 352 104, 360 76,  378 76" />
              <path className="w" d="M331 140 C 352 140, 360 190, 378 190" />

              <circle cx="134" cy="124" r="2.2" fill="#FFE6C8" className="animate-pulse" />
              <circle cx="355" cy="88" r="2.2" fill="#FFE6C8" className="animate-pulse" />
              <circle cx="355" cy="166" r="2.2" fill="#FFE6C8" className="animate-pulse" />

              {/* 粒子 1：从节点 1 (Brief解析) 飞往节点 2 (达人供稿) */}
              <circle fill="#FFE6C8" filter="url(#singleParticleGlow)">
                <animateMotion
                  dur="4s"
                  repeatCount="indefinite"
                  path="M117 126 C 133 126, 140 122, 152 122"
                  keyPoints="0;1"
                  keyTimes="0;1"
                  calcMode="linear"
                />
                <animate
                  attributeName="r"
                  dur="4s"
                  repeatCount="indefinite"
                  values="0; 2.8; 2.8; 0"
                  keyTimes="0; 0.15; 0.85; 1"
                  calcMode="linear"
                />
                <animate
                  attributeName="opacity"
                  dur="4s"
                  repeatCount="indefinite"
                  values="0; 1; 1; 0"
                  keyTimes="0; 0.15; 0.85; 1"
                  calcMode="linear"
                />
              </circle>

              {/* 粒子 2：从节点 2 (达人供稿) 飞往节点 3 (任务追踪 - 上分支) */}
              <circle fill="#FFE6C8" filter="url(#singleParticleGlow)">
                <animateMotion
                  dur="4s"
                  begin="-1.2s"
                  repeatCount="indefinite"
                  path="M331 104 C 352 104, 360 76, 378 76"
                  keyPoints="0;1"
                  keyTimes="0;1"
                  calcMode="linear"
                />
                <animate
                  attributeName="r"
                  dur="4s"
                  begin="-1.2s"
                  repeatCount="indefinite"
                  values="0; 2.8; 2.8; 0"
                  keyTimes="0; 0.15; 0.85; 1"
                  calcMode="linear"
                />
                <animate
                  attributeName="opacity"
                  dur="4s"
                  begin="-1.2s"
                  repeatCount="indefinite"
                  values="0; 1; 1; 0"
                  keyTimes="0; 0.15; 0.85; 1"
                  calcMode="linear"
                />
              </circle>

              {/* 粒子 3：从节点 2 (达人供稿) 飞往节点 4 (数据回流 - 下分支) */}
              <circle fill="#FFE6C8" filter="url(#singleParticleGlow)">
                <animateMotion
                  dur="4s"
                  begin="-2.4s"
                  repeatCount="indefinite"
                  path="M331 140 C 352 140, 360 190, 378 190"
                  keyPoints="0;1"
                  keyTimes="0;1"
                  calcMode="linear"
                />
                <animate
                  attributeName="r"
                  dur="4s"
                  begin="-2.4s"
                  repeatCount="indefinite"
                  values="0; 2.8; 2.8; 0"
                  keyTimes="0; 0.15; 0.85; 1"
                  calcMode="linear"
                />
                <animate
                  attributeName="opacity"
                  dur="4s"
                  begin="-2.4s"
                  repeatCount="indefinite"
                  values="0; 1; 1; 0"
                  keyTimes="0; 0.15; 0.85; 1"
                  calcMode="linear"
                />
              </circle>
            </svg>

            {/* 节点 1：Brief 解析 (UI 模板原版直角四角标规格形态) */}
            <div className="mg n-brief flow-node" tabIndex={0}>
              <span className="mg-name">{t("Brief Parsing", "Brief 解析")}</span>
              <div className="mg-body">
                <b className="ln w80" />
                <b className="ln w55" />
                <span className="thumb" aria-hidden="true" />
                <b className="ln w70" />
              </div>
              <div className="node-detail">
                <h4>{t("Brief Parsing", "Brief 解析")}</h4>
                <p className="detail-row"><b>{t("Brand", "品牌")}</b><span>Sony · WH-1000XM5</span></p>
                <p className="detail-row"><b>{t("Platform", "平台")}</b><span>TikTok</span></p>
                <p className="detail-row"><b>{t("Target", "人群")}</b><span>{t("Commute, Travel & Office", "通勤 · 差旅 · 办公人群")}</span></p>
                <p className="detail-row"><b>{t("Style", "风格")}</b><span>{t("ANC Test + Sound Quality", "通勤降噪实测 + 音质对比")}</span></p>
                <p className="detail-row"><b>{t("Budget", "预算")}</b><span>¥260,000</span></p>
              </div>
            </div>

            {/* 节点 2：达人供稿 (UI 模板原版 3×2 暖橙雪碧图网格) */}
            <div className="mg n-lib flow-node" tabIndex={0}>
              <span className="mg-name">{t("Creator Library", "达人供稿")}</span>
              <div className="mg-body">
                <span className="faces" aria-hidden="true">
                  <span className="face" />
                  <span className="face" />
                  <span className="face" />
                  <span className="face" />
                  <span className="face" />
                  <span className="face" />
                </span>
                <span className="libcount">{t("4000+ In Stock · 6 Matched", "4000+ 在库 · 已匹配 6")}</span>
              </div>
              <div className="node-detail lib">
                <h4>{t("Creator Library", "达人供稿")}</h4>
                <p className="detail-row"><b>Home Watt Guide</b><span>3075 {t("Followers", "粉丝")} · $0.5k</span></p>
                <p className="detail-row"><b>Energía Para Mis Padres</b><span>2608 {t("Followers", "粉丝")} · $0.3k</span></p>
                <p className="detail-row"><b>两只狗</b><span>1014 {t("Followers", "粉丝")} · ¥0.0k</span></p>
                <p className="detail-row"><b>Chloe Vane</b><span>1011 {t("Followers", "粉丝")} · ¥0.1k</span></p>
                <p className="detail-row"><b>Maisie Ward</b><span>993 {t("Followers", "粉丝")} · ¥0.0k</span></p>
                <p className="detail-row"><b>You Ask We Test</b><span>969 {t("Followers", "粉丝")} · $0.3k</span></p>
              </div>
            </div>

            {/* 节点 3：任务追踪 (UI 模板原版推进轨道与阶段刻度) */}
            <div className="mg n-task flow-node" tabIndex={0}>
              <span className="mg-name">{t("Task Tracking", "任务追踪")}</span>
              <div className="mg-body">
                <span className="track" aria-hidden="true"><i /></span>
                <span className="ticks" aria-hidden="true">
                  <i /><i /><i /><i /><i /><i />
                </span>
              </div>
              <div className="node-detail">
                <h4>{t("Task Tracking", "任务追踪")}</h4>
                <p className="detail-row"><b>{t("Task", "任务")}</b><span>Sony WH-1000XM5 {t("Creator Promotion", "达人推广")}</span></p>
                <p className="detail-row"><b>{t("Progress", "进度")}</b><span>{t("Audit Passed → Published", "平台初审 → 已发布")}</span></p>
                <p className="detail-row"><b>{t("Active", "在途")}</b><span>{t("14 Active Tasks", "14 个任务")}</span></p>
              </div>
            </div>

            {/* 节点 4：数据回流 (UI 模板原版发光折线面积图与指标条) */}
            <div className="mg n-stats flow-node" tabIndex={0}>
              <span className="mg-name">{t("Data Feedback", "数据回流")}</span>
              <div className="mg-body">
                <svg className="spark" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
                  <path className="fill" d="M3 34 L26 23 L49 18 L72 8 L97 4 L97 40 L3 40 Z" />
                  <path className="base" d="M3 39 H97" />
                  <path className="line" d="M3 34 L26 23 L49 18 L72 8 L97 4" />
                </svg>
                <span className="kv" aria-hidden="true">
                  <i>10,161</i>
                  <i>632</i>
                  <i>8.22%</i>
                </span>
              </div>
              <div className="node-detail">
                <h4>{t("Data Feedback", "数据回流")}</h4>
                <p className="detail-row"><b>{t("Views", "播放")}</b><span>10,161 → 15,318</span></p>
                <p className="detail-row"><b>{t("Likes", "点赞")}</b><span>632</span></p>
                <p className="detail-row"><b>{t("Bookmarks", "收藏")}</b><span>121</span></p>
                <p className="detail-row"><b>{t("Engagement", "互动率")}</b><span>8.22%</span></p>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-12 pb-16 sm:pt-16 sm:pb-20 lg:pt-20 lg:pb-24 px-4 sm:px-6 lg:px-8 bg-[#0b0907] overflow-hidden">
      {/* 整个第二屏容器 */}
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-8 items-center w-full">
        {/* ===================== 左侧：标题 + 纵深星轨舞台 ===================== */}
        <div data-lenis-prevent="true" className="lg:col-span-7 flex flex-col items-start justify-start w-full">
          {/* 1. 左对齐的主副标题区域 */}
          <div className="text-left mb-6 sm:mb-8 w-full flex flex-col items-start justify-start">
            <h2 className="text-3xl sm:text-4xl lg:text-[46px] font-black tracking-tight text-[#F2ECE5] leading-[1.28] sm:leading-[1.3] lg:leading-[1.32] text-left w-full">
              {t("Marketing AgentOS Powered by ", "基于 ")}
              <span
                className="bg-clip-text text-transparent font-black drop-shadow-[0_0_24px_rgba(249,115,22,0.35)]"
                style={{
                  backgroundImage: "linear-gradient(180deg, #FDBA74 0%, #F97316 70%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                OranSim
              </span>
              {t("", " 的营销智能体操作系统")}
            </h2>
            <p className="mt-3.5 sm:mt-4.5 text-sm sm:text-base lg:text-[17px] text-[#F2ECE5]/75 font-normal leading-relaxed tracking-normal text-left w-full">
              {t(
                "Every real-world feedback loops back into the predictive accuracy of the next round.",
                "每一次真实反馈，回流成下一轮的预测精度",
              )}
            </p>
          </div>

          {/* 2. 纵深星轨舞台 (严格复刻 UI 的 orbit-stage 结构) */}
          <div
            ref={stageRef}
            data-lenis-prevent="true"
            data-lenis-prevent-wheel="true"
            className="orbit-stage-ref relative w-full h-[460px] sm:h-[540px] lg:h-[600px] select-none cursor-grab active:cursor-grabbing overflow-visible"
          >
            {/* 行星背后的呼吸光晕 (仅向左侧发散，右侧纯暗) */}
            <span
              className="absolute left-[45%] top-[56%] -translate-x-1/2 -translate-y-1/2 w-[min(30vw,380px)] aspect-square rounded-full pointer-events-none opacity-60 z-0"
              style={{
                background: "radial-gradient(circle at 20% 32%, rgba(255,109,8,0.22) 0%, rgba(255,109,8,0.04) 38%, transparent 52%)",
              }}
              aria-hidden="true"
            />

            {/* 后层轨道（行星背后，深度暗淡） */}
            <div className="orbit-ring-layer orbit-ring-back" aria-hidden="true">
              <i className="orbit-band is-core"></i>
              <i className="orbit-band is-core"></i>
              <i className="orbit-band is-core"></i>
              <i className="orbit-band is-core"></i>
              <i className="orbit-band is-outer"></i>
              <i className="orbit-band is-outer is-dashed"></i>
            </div>

            {/* 行星球体（夹在前后层轨道中间，z-index: 20） */}
            <div
              className="absolute left-[45%] top-[56%] -translate-x-1/2 -translate-y-1/2 z-20 w-[min(23vw,290px)] aspect-square rounded-full overflow-hidden pointer-events-none border border-black/80 shadow-[-10px_0_24px_rgba(255,109,8,0.2)]"
              aria-hidden="true"
            >
              {/* 地球自转贴图层：2:1 全景贴图以 200% 宽度完美贴合 360° 球面，自西向东无缝自转 */}
              <div
                className="w-full h-full rounded-full"
                style={{
                  backgroundImage: `url(${PLANET_IMAGE})`,
                  backgroundSize: "200% 100%",
                  backgroundRepeat: "repeat-x",
                  backgroundPosition: "0% 50%",
                  animation: "planetSpinLoop 40s linear infinite",
                }}
              />

              {/* 左侧向阳弧光与右侧非向阳纯暗面（仅左侧有 #ff6d08 向阳弧光，右侧纯净无光晕深黑夜景） */}
              <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle at 20% 28%, rgba(255,109,8,0.24) 0%, rgba(255,109,8,0.08) 26%, transparent 52%, rgba(4,2,1,0.75) 75%, rgba(0,0,0,0.96) 100%)",
                  boxShadow:
                    "inset 16px -2px 26px rgba(255,109,8,0.65), inset 3px 0 8px rgba(255,140,40,0.9), inset -36px -16px 54px rgba(0,0,0,0.98), inset -8px 0 16px rgba(0,0,0,1)",
                }}
              />
            </div>

            {/* 前层轨道（使用 clip-path 裁切，行星前方明亮高光） */}
            <div className="orbit-ring-layer orbit-ring-front" aria-hidden="true">
              <i className="orbit-band is-core"></i>
              <i className="orbit-band is-core"></i>
              <i className="orbit-band is-core"></i>
              <i className="orbit-band is-core"></i>
              <i className="orbit-band is-outer"></i>
              <i className="orbit-band is-outer is-dashed"></i>
            </div>

            {/* 压在半球上方的主打产品 OranSim */}
            <button
              onClick={() => setActiveId("sim")}
              className={`absolute left-[38%] sm:left-[42%] lg:left-[45%] top-[18%] -translate-x-1/2 -translate-y-1/2 z-[70] flex flex-col items-start gap-2 p-0 bg-transparent border-0 text-left cursor-pointer transition-transform duration-300 hover:scale-105 ${
                activeId === "sim" ? "scale-105" : ""
              }`}
            >
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#F97316] text-[#0B0907] text-[10px] font-mono tracking-widest font-black shadow-[0_0_12px_rgba(249,115,22,0.6)]">
                <span className="w-1 h-1 rounded-full bg-[#0B0907] animate-pulse" />
                {t("Flagship", "主打产品")}
              </span>
              <span className="flex flex-col items-start gap-0.5">
                <span
                  className={`font-black tracking-tight transition-all duration-300 ${
                    activeId === "sim"
                      ? "text-white text-3xl sm:text-4xl lg:text-[46px] drop-shadow-[0_0_24px_rgba(255,255,255,0.95)]"
                      : "text-white/80 text-3xl sm:text-4xl lg:text-[46px] hover:text-white"
                  }`}
                >
                  OranSim
                </span>

                {/* OranSim 选中高光横线 */}
                <div className="relative w-full h-[3px] flex items-center justify-start my-0.5 overflow-hidden">
                  <motion.div
                    initial={false}
                    animate={{
                      width: activeId === "sim" ? "100%" : "0%",
                      opacity: activeId === "sim" ? 1 : 0,
                    }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="h-full rounded-full bg-gradient-to-r from-[#F97316] via-[#FDBA74] to-transparent shadow-[0_0_12px_#F97316]"
                  />
                </div>

                <span className="text-xs font-mono font-medium text-[#FDBA74] tracking-widest">
                  {t("Extreme Prediction", "极致预测")}
                </span>
              </span>
            </button>

            {/* 三大卫星产品在轨公转节点 (OranHub / OranGen / OranMed) - 沿 3D 椭圆轨道连续公转 */}
            {satelliteNodes.map((satId, i) => {
              const satConfig = products[satId];
              const isSelected = activeId === satId;

              // 基础相位：Hub (210°), Gen (90°), Med (330°)
              const rad = ((orbitAngle + i * STEP + BASE_OFFSET) * Math.PI) / 180;

              // 3D 椭圆几何参数 (长半轴 46%，短半轴 17.5%，倾角 -13°)
              const Rx = 46;
              const Ry = 17.5;
              const TZ = (-13 * Math.PI) / 180;

              const x0 = Rx * Math.cos(rad);
              const y0 = Ry * Math.sin(rad);

              const xRel = x0 * Math.cos(TZ) - y0 * Math.sin(TZ);
              const yRel = x0 * Math.sin(TZ) + y0 * Math.cos(TZ);

              const sinV = Math.sin(rad);
              const depth = (sinV + 1) / 2; // 0 ~ 1
              const sc = 0.88 + depth * 0.24;
              const op = 0.45 + depth * 0.55;
              const isFront = sinV > -0.15;
              const zIdx = isSelected ? 40 : isFront ? 30 : 12;

              return (
                <div
                  key={satId}
                  onClick={() => switchProduct(satId)}
                  onMouseEnter={() => (isHoveringRef.current = true)}
                  onMouseLeave={() => (isHoveringRef.current = false)}
                  style={{
                    left: `${45 + xRel}%`,
                    top: `${56 + yRel}%`,
                    transform: `translate(-50%, -50%) scale(${isSelected ? 1.18 : sc})`,
                    opacity: isSelected ? 1 : op,
                    zIndex: zIdx,
                    transformOrigin: "center center",
                  }}
                  className="absolute p-0 bg-transparent border-0 cursor-pointer flex flex-col items-center gap-0.5 select-none pointer-events-auto"
                >
                  <span
                    className={`w-2.5 h-2.5 rounded-full mb-0.5 transition-all duration-300 ${
                      isSelected
                        ? "bg-white ring-4 ring-[#F97316] shadow-[0_0_18px_#F97316]"
                        : "bg-[#FDBA74]/80 shadow-[0_0_8px_rgba(249,115,22,0.6)]"
                    }`}
                  />
                  <span
                    className={`font-black tracking-tight whitespace-nowrap transition-all duration-300 ${
                      isSelected
                        ? "text-white text-2xl sm:text-3xl drop-shadow-[0_0_24px_rgba(255,255,255,0.95)]"
                        : "text-white/60 text-xl sm:text-2xl hover:text-white"
                    }`}
                  >
                    {satConfig.name}
                  </span>

                  {/* 卫星选中高光横线 (Selected Indicator Line) */}
                  <div className="relative w-full h-[2.5px] flex items-center justify-center my-0.5 overflow-hidden">
                    <motion.div
                      initial={false}
                      animate={{
                        width: isSelected ? "100%" : "0%",
                        opacity: isSelected ? 1 : 0,
                      }}
                      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="h-full rounded-full bg-gradient-to-r from-transparent via-[#F97316] to-transparent shadow-[0_0_10px_#F97316]"
                    />
                  </div>

                  <span
                    className={`text-xs font-mono font-medium tracking-wider whitespace-nowrap transition-colors duration-300 ${
                      isSelected ? "text-[#FDBA74]" : "text-white/40 hover:text-white/70"
                    }`}
                  >
                    {t(satConfig.tagline.en, satConfig.tagline.zh)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ===================== 右侧：高饱和橙色交互控制台卡片 ===================== */}
        <div className="lg:col-span-5 w-full mt-4 lg:mt-0">
          <motion.div
            key={currentProduct.id}
            initial={{ opacity: 0, y: 15, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[24px] bg-gradient-to-br from-[#FB923C] via-[#F97316] to-[#EA6A0A] border border-[#F97316]/60 p-5 sm:p-7 shadow-[0_30px_80px_rgba(249,115,22,0.24)] overflow-hidden flex flex-col justify-between min-h-[500px] sm:min-h-[540px] lg:min-h-[570px]"
          >
            {/* 1. 卡片头部 Header */}
            <div>
              <h3 className="text-3xl sm:text-4xl lg:text-[44px] font-black tracking-tight text-[#180C02] leading-none">
                {currentProduct.name}
              </h3>
              <p className="text-sm sm:text-base font-bold text-[#180C02]/90 mt-2 tracking-wider">
                {t(currentProduct.tagline.en, currentProduct.tagline.zh)}
              </p>
            </div>

            {/* 2. 2x2 核心能力指标矩阵 (数值在上，mono 标签在下，细分割线) */}
            <div className="grid grid-cols-2 border-y border-[#180C02]/25 my-4 sm:my-5">
              {currentProduct.metrics.map((metric, idx) => (
                <div
                  key={idx}
                  className={`py-2.5 sm:py-3 flex flex-col ${
                    idx % 2 === 1 ? "border-l border-[#180C02]/20 pl-4 sm:pl-5" : "pr-4 sm:pr-5"
                  } ${idx < 2 ? "border-b border-[#180C02]/15" : ""}`}
                >
                  <span className="text-lg sm:text-xl font-black tracking-tight text-[#180C02] leading-snug truncate">
                    {t(metric.value.en, metric.value.zh)}
                  </span>
                  <b className="text-[11px] font-mono font-medium tracking-wider text-[#180C02]/85 mt-1">
                    {t(metric.label.en, metric.label.zh)}
                  </b>
                </div>
              ))}
            </div>

            {/* 3. 中央暗色流程图可视化容器 (统一应用官方深烬橙复合渐变底色 .oviz-slot，全量产品无死角铺满) */}
            <div className="oviz-slot w-full mb-4 sm:mb-5 flex-1 flex items-center justify-center min-h-[220px] sm:min-h-[240px] p-2.5 sm:p-3.5">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentProduct.id}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.25 }}
                  className="w-full h-full flex items-center justify-center min-h-[220px] sm:min-h-[240px]"
                >
                  {renderVisualizer(currentProduct.id)}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* 4. 所有产品入口复用第一屏立即体验的登录校验和 Toolbox 跳转逻辑 */}
            <div>
              <button
                type="button"
                onClick={handleEnter}
                disabled={isNavigating}
                className="inline-flex items-center justify-center gap-2.5 px-6 py-3 rounded-full bg-[#180C02] text-[#FDBA74] hover:text-white hover:bg-black font-bold text-sm sm:text-base tracking-wide shadow-[0_12px_30px_rgba(24,12,2,0.34)] hover:-translate-y-0.5 transition-all duration-300 group cursor-pointer border-0 disabled:opacity-75 disabled:cursor-not-allowed"
              >
                <span>
                  {isNavigating
                    ? t("Redirecting…", "跳转中…")
                    : t(currentProduct.btnText.en, currentProduct.btnText.zh)}
                </span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AgentOSSection;
